import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil } from "lucide-react";

/** Admin-only dialog to edit an existing staff/admin account's name,
 *  username, role, and (optionally) reset the password.
 *  Super-admin accounts have their `active`, `role`, and `username` fields
 *  locked at the server; we mirror that in the UI so lesser admins see
 *  greyed-out fields rather than a 403 when saving. */
export default function EditStaffDialog({ user, currentUserId, onSaved, trigger }) {
  const isSuper = !!user.super_admin;
  const isSelf = user.id === currentUserId;
  const locked = isSuper && !isSelf;

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user.name || "",
    username: user.username || "",
    role: user.role || "staff",
    password: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: user.name || "",
        username: user.username || "",
        role: user.role || "staff",
        password: "",
      });
    }
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    // Build a partial payload with only the fields the admin actually changed.
    const payload = {};
    if (form.name.trim() && form.name !== user.name) payload.name = form.name.trim();
    if (!locked && form.username.trim() && form.username !== user.username) {
      payload.username = form.username.trim();
    }
    if (!locked && form.role !== user.role) payload.role = form.role;
    if (form.password) payload.password = form.password;
    if (Object.keys(payload).length === 0) {
      toast.info("Nothing to update");
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/staff/${user.id}`, payload);
      toast.success(`${data.name} updated`);
      onSaved?.(data);
      setOpen(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-8"
            data-testid={`edit-staff-btn-${user.username}`}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid={`edit-staff-dialog-${user.username}`}>
        <DialogHeader>
          <DialogTitle>
            Edit account · <span className="font-mono">{user.username}</span>
          </DialogTitle>
        </DialogHeader>
        {isSuper && (
          <div className="rounded-xl p-3 text-xs"
               style={{ background: "rgba(201,162,75,0.10)", color: "var(--text-muted)" }}>
            {isSelf
              ? "You are the super admin. Only you can edit these fields on your own account."
              : "This is the super admin account. Role, username, and active status are locked."}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid={`edit-staff-name-${user.username}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Username</Label>
              <Input
                value={form.username}
                disabled={locked}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                data-testid={`edit-staff-username-${user.username}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
                disabled={locked}
              >
                <SelectTrigger data-testid={`edit-staff-role-${user.username}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin / Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Reset password <span className="label-caps ml-1">optional</span></Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Leave blank to keep the current password"
              autoComplete="new-password"
              data-testid={`edit-staff-password-${user.username}`}
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={saving}
              data-testid={`edit-staff-save-${user.username}`}
              className="rounded-full text-white"
              style={{ background: "var(--navy)" }}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
